/**
 * AdminWishEditForm Component
 * Simplified form for admin to edit wish content
 */

import { useState, useEffect } from 'react';
import './AdminWishEditForm.css';

export interface AdminWishFormData {
  title: string;
  description: string;
  category: string;
  region: string;
  district: string;
  budget: string;
  urgent: boolean;
}

interface AdminWishEditFormProps {
  initialData?: Partial<AdminWishFormData>;
  onSubmit?: (data: AdminWishFormData) => Promise<void>;
  isLoading?: boolean;
}

const CATEGORIES = ['維修', '保養', '美容', '改裝'];
const REGIONS = ['台北市', '新北市', '桃園市', '台中市', '台南市', '高雄市'];

export const AdminWishEditForm: React.FC<AdminWishEditFormProps> = ({
  initialData,
  onSubmit,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<AdminWishFormData>({
    title: initialData?.title || '',
    description: initialData?.description || '',
    category: initialData?.category || CATEGORIES[0],
    region: initialData?.region || REGIONS[0],
    district: initialData?.district || '',
    budget: initialData?.budget || '',
    urgent: initialData?.urgent || false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        description: initialData.description || '',
        category: initialData.category || CATEGORIES[0],
        region: initialData.region || REGIONS[0],
        district: initialData.district || '',
        budget: initialData.budget || '',
        urgent: initialData.urgent || false,
      });
    }
  }, [initialData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = '標題是必填欄位';
    }
    if (!formData.category) {
      newErrors.category = '請選擇服務類別';
    }
    if (!formData.region) {
      newErrors.region = '請選擇地區';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit?.(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formId = 'wish-form';

  return (
    <form id={formId} onSubmit={handleSubmit} className="admin-wish-edit-form">
      {/* Title */}
      <div className="admin-wish-edit-form__field">
        <label htmlFor="title" className="admin-wish-edit-form__label">
          許願標題 *
        </label>
        <input
          type="text"
          id="title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          className={`admin-wish-edit-form__input ${
            errors.title ? 'admin-wish-edit-form__input--error' : ''
          }`}
          placeholder="例如：需要汽車定期保養服務"
          disabled={isSubmitting || isLoading}
        />
        {errors.title && (
          <span className="admin-wish-edit-form__error">{errors.title}</span>
        )}
      </div>

      {/* Description */}
      <div className="admin-wish-edit-form__field">
        <label htmlFor="description" className="admin-wish-edit-form__label">
          需求描述
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="admin-wish-edit-form__textarea"
          placeholder="請詳細描述需求..."
          rows={4}
          disabled={isSubmitting || isLoading}
        />
      </div>

      {/* Category & Region */}
      <div className="admin-wish-edit-form__row">
        <div className="admin-wish-edit-form__field">
          <label htmlFor="category" className="admin-wish-edit-form__label">
            服務類別 *
          </label>
          <select
            id="category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            className={`admin-wish-edit-form__select ${
              errors.category ? 'admin-wish-edit-form__input--error' : ''
            }`}
            disabled={isSubmitting || isLoading}
          >
            {CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
          {errors.category && (
            <span className="admin-wish-edit-form__error">{errors.category}</span>
          )}
        </div>

        <div className="admin-wish-edit-form__field">
          <label htmlFor="region" className="admin-wish-edit-form__label">
            地區 *
          </label>
          <select
            id="region"
            name="region"
            value={formData.region}
            onChange={handleChange}
            className={`admin-wish-edit-form__select ${
              errors.region ? 'admin-wish-edit-form__input--error' : ''
            }`}
            disabled={isSubmitting || isLoading}
          >
            {REGIONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          {errors.region && (
            <span className="admin-wish-edit-form__error">{errors.region}</span>
          )}
        </div>
      </div>

      {/* District */}
      <div className="admin-wish-edit-form__field">
        <label htmlFor="district" className="admin-wish-edit-form__label">
          行政區
        </label>
        <input
          type="text"
          id="district"
          name="district"
          value={formData.district}
          onChange={handleChange}
          className="admin-wish-edit-form__input"
          placeholder="例如：信義區"
          disabled={isSubmitting || isLoading}
        />
      </div>

      {/* Budget */}
      <div className="admin-wish-edit-form__field">
        <label htmlFor="budget" className="admin-wish-edit-form__label">
          預算範圍
        </label>
        <input
          type="text"
          id="budget"
          name="budget"
          value={formData.budget}
          onChange={handleChange}
          className="admin-wish-edit-form__input"
          placeholder="例如：3000-5000"
          disabled={isSubmitting || isLoading}
        />
        <span className="admin-wish-edit-form__hint">格式：最低-最高（例如：3000-5000）</span>
      </div>

      {/* Urgent */}
      <div className="admin-wish-edit-form__field admin-wish-edit-form__field--checkbox">
        <label className="admin-wish-edit-form__checkbox-label">
          <input
            type="checkbox"
            name="urgent"
            checked={formData.urgent}
            onChange={handleChange}
            className="admin-wish-edit-form__checkbox"
            disabled={isSubmitting || isLoading}
          />
          <span>這是一個急件</span>
        </label>
      </div>
    </form>
  );
};

export default AdminWishEditForm;
